import { useCallback, useEffect, useState } from 'react';

/**
 * A horizontally scrolling row (e.g. tabs) that hides its scrollbar:
 * reports which edges have hidden content so they can fade, and keeps the
 * active child (`[data-state=active]`) in view whenever `activeKey` changes.
 */
export function useScrollableRow(activeKey: unknown) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    if (!el) return;
    // A few pixels of slack: fractional widths can leave the row a hair short of its end
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, [el]);

  useEffect(() => {
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [el, update]);

  // Bring the active item into view together with its neighbours, so the next tab is always reachable
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeKey is the trigger to re-scroll
  useEffect(() => {
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[data-state=active]');
    if (!active) return;
    const rowLeft = el.getBoundingClientRect().left - el.scrollLeft;
    const left = (node: Element) => node.getBoundingClientRect().left - rowLeft;
    const right = (node: Element) => node.getBoundingClientRect().right - rowLeft;
    const prev = active.previousElementSibling;
    const next = active.nextElementSibling;
    // First and last tabs pin the row to its edge, so no fade covers them
    // Don't return scrollTo's result: newer browsers return a Promise, which React would treat as a cleanup
    if (!prev) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (!next) {
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      return;
    }
    let start = left(prev);
    let end = right(next);
    // Not enough room for both neighbours: center the active tab instead
    if (end - start > el.clientWidth) {
      const center = (left(active) + right(active)) / 2;
      start = center - el.clientWidth / 2;
      end = start + el.clientWidth;
    }
    if (start < el.scrollLeft) el.scrollTo({ left: start, behavior: 'smooth' });
    else if (end > el.scrollLeft + el.clientWidth)
      el.scrollTo({ left: end - el.clientWidth, behavior: 'smooth' });
  }, [el, activeKey]);

  // Fade only the edges that actually hide content
  const maskImage =
    edges.left || edges.right
      ? `linear-gradient(to right, ${edges.left ? 'transparent, black 32px' : 'black'}, ${
          edges.right ? 'black calc(100% - 32px), transparent' : 'black'
        })`
      : undefined;

  return { ref: setEl, style: maskImage ? { maskImage, WebkitMaskImage: maskImage } : undefined };
}
